import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../utils/api.js";
import Toastify from "../utils/Toastify.js";

const initialState = {
    isRefresh: false,
    isLoading: false,
    notificationData: [],
    notificationCountData: 0,
    createdNotificationData:{},
    errorMsg: "",
    isError: false,
};

const countUnread = (list) => (list || []).filter(n => n && !n.isRead).length;

export const getNotification = createAsyncThunk('getNotification', async ({body,showAll}, { rejectWithValue, dispatch }) => {
    try {
        const { data, status } = await api.getNotification(body,showAll);
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
}
);

export const getNotificationCount = createAsyncThunk('getNotificationCount', async (body, { rejectWithValue, dispatch }) => {
    try {
        const { data, status } = await api.getNotificationCount(body);
        if (status === 200) {
            dispatch(setNotificationCount(data.data))
        }
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
}
);

export const markAsRead=createAsyncThunk('markAsRead',async(body, { rejectWithValue, dispatch })=>{
    try {
        const { data, status } = await api.markAsRead(body);
        if (status === 200) {
            dispatch(updateNotificationStatus(body))
        }
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
});

export const markAllAsRead=createAsyncThunk('markAllAsRead',async(body,{rejectWithValue,dispatch})=>{
    try {
        const { data, status } = await api.markAllAsReaded(body);
        if(status === 200){
            dispatch(updateAllNotificationMarkAsRead())
            Toastify.success("Marked all notifications as read ✅ ");
        }
        return data.data;

    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
});

// Opening the notification centre counts as seeing everything: clear the badge
// and mark them read on the server, without a toast
export const markAllSeen = createAsyncThunk('markAllSeen', async (userId, { rejectWithValue, dispatch }) => {
    try {
        dispatch(setNotificationCount(0));
        const { data } = await api.markAllAsReaded(userId);
        return data.data;
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || "'Something went wrong. Please try again later.'")
    }
});

export const notificationSlice = createSlice({
    name: "notification",
    initialState,
    reducers: {
        setNotification: (state, action) => {
            if (Array.isArray(action.payload)) {
                state.notificationData = action.payload;
                state.notificationCountData = countUnread(action.payload);
            }
        },
        setCreatednotification:(state,action)=>{
             state.createdNotificationData=action.payload
        },
        setNotificationCount: (state, action) => {
            const count = typeof action.payload === 'number' ? action.payload : action.payload?.count;
            state.notificationCountData = typeof count === 'number' ? count : 0;
        },
        setRefresh:(state) => {
            state.isRefresh = !state.isRefresh
        },
        addNotification: (state, action) => {
            const notification = action.payload;
            if (!notification || typeof notification !== 'object') return;
            if (notification._id && state.notificationData.some(n => n && n._id === notification._id)) return;

            state.notificationData = [notification, ...state.notificationData];
            if (!notification.isRead) {
                state.notificationCountData += 1;
            }
        },
        updateNotificationStatus:(state,action)=>{
            const idToMark = action.payload;

            let wasUnread = false;
            state.notificationData = state.notificationData?.map((notification) => {
                if (notification && notification._id === idToMark) {
                    if (!notification.isRead) {
                        wasUnread = true;
                    }
                    return {
                        ...notification,
                        isRead: true
                    };
                }
                return notification;
            });

            if (wasUnread) {
                state.notificationCountData = Math.max(0, state.notificationCountData - 1);
            }
        },
        updateAllNotificationMarkAsRead:(state,action)=>{
            state.notificationData = state.notificationData?.map((notification) => {
                if (notification && typeof notification === 'object') {
                    return {
                        ...notification,
                        isRead: true
                    };
                }
                return notification;
            });
            state.notificationCountData = 0;
        },
        // Clears the previous user's notifications (logout / account switch)
        resetNotifications: () => initialState
    },

    extraReducers: (builder) => {
        builder.addCase(getNotification.pending, (state) => {
            state.isLoading = true;
            state.isError = false;
        });
        builder.addCase(getNotification.fulfilled, (state, action) => {
            state.isLoading = false;
            state.notificationData = Array.isArray(action.payload) ? action.payload : [];
        });
        builder.addCase(getNotification.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.errorMsg = action.payload;
        });
        builder.addCase('customer/setLoggedIn', (state, action) => {
            if (action.payload === false) return initialState;
        });
    }
});

export const { setNotification, setNotificationCount, setRefresh, addNotification, updateNotificationStatus, updateAllNotificationMarkAsRead, setCreatednotification, resetNotifications } = notificationSlice.actions;

export default notificationSlice.reducer;
